import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { orders } from "@/api/orders";
import { useActiveAccount } from "@/components/ActiveAccount";
import { usePreferences } from "@/hooks/usePreferences";
import { Badge, Button, Input, Label, Select } from "@/components/ui";
import { useToast } from "@/components/Toast";
import type { WatchSymbol } from "./WatchlistPane";

interface TicketPreset {
  name: string;
  side: "BUY" | "SELL";
  type: "MARKET" | "LIMIT";
  qty: string;
}

type Side = "BUY" | "SELL";
type OrderType = "MARKET" | "LIMIT";

/**
 * Compact order entry pane wired up for hotkey trading.
 *
 *   B / S  — toggle BUY / SELL
 *   1–9    — set quantity directly (digits typed go into the qty field)
 *   Enter  — submit
 *   Esc    — clear focus
 *
 * The pane only listens for hotkeys when one of its own inputs is
 * focused, so typing in the watchlist or chart doesn't accidentally
 * fire orders.
 */
export function OrderTicketPane({ symbol }: { symbol: WatchSymbol }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { active } = useActiveAccount();
  const prefs = usePreferences();

  const [side, setSide] = useState<Side>("BUY");
  const [qty, setQty] = useState("1");
  const [type, setType] = useState<OrderType>("MARKET");
  const [limit, setLimit] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [bracket, setBracket] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const presets: TicketPreset[] = Array.isArray((prefs.data as Record<string, unknown>).ticket_presets)
    ? ((prefs.data as Record<string, unknown>).ticket_presets as TicketPreset[])
    : [];

  function applyPreset(p: TicketPreset) {
    setSide(p.side);
    setType(p.type);
    setQty(p.qty);
  }

  function saveCurrentAsPreset() {
    const name = prompt("Preset name?")?.trim();
    if (!name) return;
    const updated = [
      ...presets.filter((p) => p.name !== name),
      { name, side, type, qty },
    ].slice(-12); // cap to 12 most-recent
    prefs.patch({ ticket_presets: updated });
    toast.push(`Preset "${name}" saved`, "success");
  }

  function deletePreset(name: string) {
    const updated = presets.filter((p) => p.name !== name);
    prefs.patch({ ticket_presets: updated });
  }

  const submit = useMutation({
    mutationFn: async () => {
      const res = await orders.create({
        symbol: symbol.symbol,
        asset_class: symbol.asset_class,
        side,
        order_type: type,
        quantity: Number(qty),
        limit_price: type === "LIMIT" ? Number(limit) : null,
        strategy_id: "manual",
        // Routes through routing_engine + matcher when set; falls back
        // to legacy user-broker path otherwise.
        trading_account_id: active?.id,
      });
      // Bracket — fire one modify after the order has an id so SL/TP
      // attach without a second user round-trip.
      if (bracket && (sl || tp)) {
        try {
          await orders.modify(res.order_id, {
            stop_loss: sl ? Number(sl) : null,
            take_profit: tp ? Number(tp) : null,
          });
        } catch (e) {
          // Don't surface a second toast — the order itself succeeded.
          // Caller can adjust SL/TP from the positions pane.
          console.warn("bracket modify failed", e);
        }
      }
      return res;
    },
    onSuccess: (r) => {
      toast.push(`${r.message} via ${r.routed_via}`, "success");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
      qc.invalidateQueries({ queryKey: ["margin"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  // Hotkeys: only fire while focus is inside this pane.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const root = formRef.current;
      if (!root) return;
      if (!root.contains(document.activeElement)) return;

      if (e.key === "Escape") {
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }
      if (e.key.toLowerCase() === "b") {
        e.preventDefault();
        setSide("BUY");
      } else if (e.key.toLowerCase() === "s") {
        e.preventDefault();
        setSide("SELL");
      } else if (e.key === "Enter" && !e.shiftKey) {
        // Fall through — the form's onSubmit handles it.
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
      className="flex h-full flex-col gap-2 p-2"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-2xs uppercase tracking-wider text-term-mute">Symbol</div>
          <div className="tnum text-sm font-semibold text-term-fg">{symbol.symbol}</div>
        </div>
        <div className="text-right">
          <div className="text-2xs text-term-mute">
            <kbd className="rounded-sm bg-term-elev px-1">B</kbd>uy /{" "}
            <kbd className="rounded-sm bg-term-elev px-1">S</kbd>ell
          </div>
          <div className="mt-0.5 text-2xs">
            {active ? (
              <Badge tone="good">acct {active.login_code}</Badge>
            ) : (
              <Badge tone="warn">no account</Badge>
            )}
          </div>
        </div>
      </div>

      {(presets.length > 0 || true) && (
        <div className="flex flex-wrap items-center gap-1 border-y border-term-border py-1">
          {presets.map((p) => (
            <span key={p.name} className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => applyPreset(p)}
                title={`${p.side} ${p.qty} ${p.type}`}
                className="rounded-sm border border-term-border bg-term-elev px-1.5 py-0.5 text-2xs text-term-fg hover:bg-term-border-strong"
              >
                {p.name}
              </button>
              <button
                type="button"
                onClick={() => deletePreset(p.name)}
                aria-label={`Delete preset ${p.name}`}
                className="text-2xs text-term-mute hover:text-term-fg"
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={saveCurrentAsPreset}
            className="ml-auto rounded-sm border border-dashed border-term-border px-1.5 py-0.5 text-2xs uppercase tracking-wider text-term-mute hover:text-term-fg"
          >
            + Save
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-1">
        <button
          type="button"
          onClick={() => setSide("BUY")}
          className={`rounded-sm py-1.5 text-xs font-semibold transition-colors ${
            side === "BUY" ? "bg-term-up text-black" : "border border-term-border text-term-up"
          }`}
        >
          BUY
        </button>
        <button
          type="button"
          onClick={() => setSide("SELL")}
          className={`rounded-sm py-1.5 text-xs font-semibold transition-colors ${
            side === "SELL" ? "bg-term-down text-white" : "border border-term-border text-term-down"
          }`}
        >
          SELL
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Qty</Label>
          <Input
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))}
          />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={type} onChange={(e) => setType(e.target.value as OrderType)}>
            <option value="MARKET">MARKET</option>
            <option value="LIMIT">LIMIT</option>
          </Select>
        </div>
      </div>

      {type === "LIMIT" && (
        <div>
          <Label>Limit price</Label>
          <Input
            inputMode="decimal"
            value={limit}
            onChange={(e) => setLimit(e.target.value.replace(/[^0-9.]/g, ""))}
          />
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-term-border pt-2">
        <input
          id="bracket"
          type="checkbox"
          checked={bracket}
          onChange={(e) => setBracket(e.target.checked)}
        />
        <label htmlFor="bracket" className="text-2xs uppercase tracking-wider text-term-dim">
          Bracket (attach SL/TP)
        </label>
      </div>
      {bracket && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Stop loss</Label>
            <Input
              inputMode="decimal"
              value={sl}
              onChange={(e) => setSl(e.target.value.replace(/[^0-9.]/g, ""))}
            />
          </div>
          <div>
            <Label>Take profit</Label>
            <Input
              inputMode="decimal"
              value={tp}
              onChange={(e) => setTp(e.target.value.replace(/[^0-9.]/g, ""))}
            />
          </div>
        </div>
      )}

      <Button
        type="submit"
        variant={side === "BUY" ? "buy" : "sell"}
        loading={submit.isPending}
        className="mt-auto w-full py-2 text-sm"
      >
        {side} {qty} {symbol.symbol} ({type})
      </Button>
      <div className="text-2xs text-term-mute">
        <kbd className="rounded-sm bg-term-elev px-1">Enter</kbd> submits ·{" "}
        <kbd className="rounded-sm bg-term-elev px-1">Esc</kbd> blurs
      </div>
    </form>
  );
}
